import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Shield, Award, MapPin, Building, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicProfile = () => {
  const { professionalId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/profile/public/${professionalId}`)
      .then(r => setProfile(r.data))
      .catch(e => setError(e.response?.status === 403 ? 'Perfil no publico' : 'Profesional no encontrado'))
      .finally(() => setLoading(false));
  }, [professionalId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F6F5F0' }}>
      <Loader2 className="w-10 h-10 animate-spin" style={{ color: '#C5A55A' }} />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#F6F5F0' }}>
      <div className="text-center">
        <p className="text-lg text-gray-500 mb-4">{error}</p>
        <Link to="/" style={{ color: '#C5A55A' }} className="hover:underline">Volver al inicio</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#F6F5F0' }}>
      {/* Header */}
      <header style={{ background: '#0B1D3A' }}>
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" style={{ color: '#fff', textDecoration: 'none' }}>
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '2px' }}>
              CERTILEX<sup style={{ fontSize: '0.5em' }}>&reg;</sup>
            </h1>
          </Link>
          <Link to="/" className="flex items-center gap-1 text-sm" style={{ color: '#C5A55A' }}>
            <ArrowLeft className="w-4 h-4" /> Inicio
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-3xl">
        <Card style={{ border: '2px solid #E2DDD3', overflow: 'hidden' }}>
          {/* Profile Header */}
          <div style={{ background: '#0B1D3A', padding: '2.5rem 2rem', position: 'relative' }}>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(197,165,90,0.15)', border: '3px solid #C5A55A' }}>
                {profile.profile_photo_url ? (
                  <img src={profile.profile_photo_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span style={{ color: '#C5A55A', fontSize: '2rem', fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700 }}>
                    {profile.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#fff', fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {profile.full_name}
                </h2>
                <p style={{ color: '#C5A55A', fontSize: '1rem', marginTop: '0.25rem' }}>{profile.specialty}</p>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  {profile.collegiate_number && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      <Shield className="w-3 h-3" /> Col. {profile.collegiate_number}
                    </span>
                  )}
                  {profile.official_college && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      <Building className="w-3 h-3" /> {profile.official_college}
                    </span>
                  )}
                  {profile.country && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      <MapPin className="w-3 h-3" /> {profile.country}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {profile.is_certified && (
              <div className="absolute top-4 right-4">
                <div className="flex items-center gap-1 px-3 py-1 rounded-full" style={{ background: '#C5A55A', color: '#0B1D3A' }}>
                  <Award className="w-4 h-4" />
                  <span className="text-xs font-bold">CERTIFICADO</span>
                </div>
              </div>
            )}
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Certified Competencies */}
            {profile.certified_competencies?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2" style={{ color: '#0B1D3A', fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Competencias Certificadas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.certified_competencies.map((c, i) => (
                    <Badge key={i} className="px-3 py-1" style={{ background: '#0B1D3A', color: '#C5A55A' }}>{c}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Areas */}
            {profile.areas_to_certify?.length > 0 && !profile.certified_competencies?.length && (
              <div>
                <h3 className="font-semibold text-sm mb-2" style={{ color: '#0B1D3A', fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Areas de Competencia
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.areas_to_certify.map((a, i) => (
                    <Badge key={i} variant="outline">{a}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Acta Notarial */}
            {profile.acta_notarial && (
              <div className="p-4 rounded-lg" style={{ background: '#FFF7ED', border: '1px solid #C5A55A' }}>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" style={{ color: '#C5A55A' }} />
                  <div>
                    <p className="font-semibold text-sm" style={{ color: '#0B1D3A' }}>Acta Notarial registrada</p>
                    <p className="text-xs" style={{ color: '#4A4A5A' }}>
                      {profile.acta_notarial.filename} — {profile.acta_notarial.date?.slice(0, 10)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Verification badge */}
            <div className="text-center pt-4" style={{ borderTop: '1px solid #E2DDD3' }}>
              <p className="text-xs" style={{ color: '#9ca3af' }}>
                Perfil gestionado por CERTILEX — Gestion Notarial y Registro Colegial
              </p>
              <p className="text-xs mt-1" style={{ color: '#C5A55A', fontWeight: 600 }}>certilex.es</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicProfile;
